import React, { useState } from 'react';
import {
  Row,
  Col,
  Progress,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import HlsVideoPlayer from './HlsVideoPlayer';
import PortraitMediaFrame from './PortraitMediaFrame';
import ThumbnailPreviewFrame from './ThumbnailPreviewFrame';
import MediaTile from './MediaTile';
import type { ModuleUploadStatus } from './ModuleItemCard';

export interface SingleContentMediaState {
  file: File | null;
  thumbnail: File | null;
  existingFileUrl: string;
  existingFileType: 'audio' | 'video' | '';
  thumbnailPreview: string;
  mediaStatus?: string;
  mediaErrorMessage?: string;
  fileUploadStatus?: ModuleUploadStatus;
  fileUploadProgress?: number;
  fileUploadError?: string;
  thumbnailUploadStatus?: ModuleUploadStatus;
  thumbnailUploadProgress?: number;
  thumbnailUploadError?: string;
  removingTarget?: 'file' | 'thumbnail' | null;
}

interface SingleContentMediaCardProps {
  contentId?: string;
  hasTitle: boolean;
  media: SingleContentMediaState;
  onUploadMedia: (payload: { file?: File; thumbnail?: File }) => void;
  onRemoveMedia: (target: 'file' | 'thumbnail') => void;
}

const mediaStatusLabel = (status?: string): { color: string; text: string } | null => {
  switch (status) {
    case 'pending':
      return { color: 'warning', text: 'Transcode queued' };
    case 'processing':
      return { color: 'info', text: 'Transcoding…' };
    case 'ready':
      return { color: 'success', text: 'Ready' };
    case 'failed':
      return { color: 'danger', text: 'Transcode failed' };
    default:
      return null;
  }
};

const SingleContentMediaCard: React.FC<SingleContentMediaCardProps> = ({
  contentId,
  hasTitle,
  media,
  onUploadMedia,
  onRemoveMedia,
}) => {
  const [confirmRemove, setConfirmRemove] = useState<'file' | 'thumbnail' | null>(null);
  const transcodeBadge = mediaStatusLabel(media.mediaStatus);
  const isTargetUploading = (target: 'file' | 'thumbnail') =>
    target === 'file'
      ? media.fileUploadStatus === 'uploading'
      : media.thumbnailUploadStatus === 'uploading';
  const isTargetBusy = (target: 'file' | 'thumbnail') =>
    media.removingTarget === target || isTargetUploading(target);
  const fileUploadDisabled = !hasTitle || isTargetBusy('file');
  const thumbnailUploadDisabled = !hasTitle || isTargetBusy('thumbnail');
  const hasMediaFile = Boolean(media.file || media.existingFileUrl);
  const hasThumbnail = Boolean(media.thumbnail || media.thumbnailPreview);

  const isServerBacked = (target: 'file' | 'thumbnail') =>
    Boolean(contentId) &&
    (target === 'file'
      ? Boolean(media.existingFileUrl && !media.file)
      : Boolean(media.thumbnailPreview && !media.thumbnail));

  const requestRemove = (target: 'file' | 'thumbnail') => {
    if (isTargetBusy(target)) return;
    if (isServerBacked(target)) {
      setConfirmRemove(target);
      return;
    }
    onRemoveMedia(target);
  };

  const confirmRemoveMedia = () => {
    if (confirmRemove) {
      onRemoveMedia(confirmRemove);
      setConfirmRemove(null);
    }
  };

  const onMediaDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    onUploadMedia({ file: acceptedFiles[0] });
  };

  const onThumbnailDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    onUploadMedia({ thumbnail: acceptedFiles[0] });
  };

  const {
    getRootProps: getFileProps,
    getInputProps: getFileInputProps,
    isDragActive: isFileDragActive,
    isDragAccept: isFileDragAccept,
    isDragReject: isFileDragReject,
  } = useDropzone({
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/wav': ['.wav'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
    },
    maxFiles: 1,
    disabled: fileUploadDisabled,
    onDrop: onMediaDrop,
  });

  const {
    getRootProps: getThumbnailProps,
    getInputProps: getThumbnailInputProps,
    isDragActive: isThumbnailDragActive,
    isDragAccept: isThumbnailDragAccept,
    isDragReject: isThumbnailDragReject,
  } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'] },
    maxFiles: 1,
    disabled: thumbnailUploadDisabled,
    onDrop: onThumbnailDrop,
  });

  return (
    <>
      {contentId ? (
        <Alert color="success" className="mb-4 py-2">
          <i className="ri-upload-cloud-2-line me-2"></i>
          Drop audio/video or thumbnails to upload immediately. Use Remove on each tile to clear media.
        </Alert>
      ) : (
        <Alert color="warning" className="mb-4 py-2">
          <i className="ri-upload-cloud-line me-2"></i>
          Save content once, then drop files here to upload automatically.
        </Alert>
      )}

      <Row>
        {(media.fileUploadStatus === 'uploading' ||
          media.thumbnailUploadStatus === 'uploading' ||
          media.fileUploadStatus === 'error' ||
          media.thumbnailUploadStatus === 'error') && (
          <Col md={12}>
            {media.fileUploadStatus === 'uploading' && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small mb-1">
                  <span className="text-muted">Uploading video…</span>
                  <span className="text-muted">{media.fileUploadProgress ?? 0}%</span>
                </div>
                <Progress value={media.fileUploadProgress ?? 0} color="primary" />
              </div>
            )}
            {media.thumbnailUploadStatus === 'uploading' && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small mb-1">
                  <span className="text-muted">Uploading thumbnail…</span>
                  <span className="text-muted">{media.thumbnailUploadProgress ?? 0}%</span>
                </div>
                <Progress value={media.thumbnailUploadProgress ?? 0} color="info" />
              </div>
            )}
            {media.fileUploadStatus === 'error' && media.fileUploadError && (
              <Alert color="danger" className="py-2 small mb-3">
                Video upload failed: {media.fileUploadError}. Drop the file again or save to retry.
              </Alert>
            )}
            {media.thumbnailUploadStatus === 'error' && media.thumbnailUploadError && (
              <Alert color="danger" className="py-2 small mb-3">
                Thumbnail upload failed: {media.thumbnailUploadError}. Drop the image again or save to retry.
              </Alert>
            )}
          </Col>
        )}

        <Col md={12} className="mb-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="small fw-semibold text-muted">Content media</span>
            {hasMediaFile && (
              <Badge color="primary" className="fw-normal">
                {media.existingFileType === 'video'
                  ? 'Video'
                  : media.existingFileType === 'audio'
                    ? 'Audio'
                    : media.file?.type.startsWith('video/')
                      ? 'Video'
                      : media.file
                        ? 'Audio'
                        : 'Media'}
              </Badge>
            )}
            {transcodeBadge && (
              <Badge color={transcodeBadge.color} className="fw-normal">
                {transcodeBadge.text}
              </Badge>
            )}
            <span className="small text-muted">Vertical video · any thumbnail shape</span>
            {!hasTitle && (
              <span className="small text-warning">
                <i className="ri-information-line me-1" />
                Add a title above to enable uploads
              </span>
            )}
          </div>
        </Col>

        {media.mediaStatus === 'failed' && (
          <Col md={12}>
            <Alert color="warning" className="py-2 small mb-2">
              <strong>Transcode failed.</strong>{' '}
              {media.mediaErrorMessage ||
                'The video uploaded, but converting it to streaming (HLS) failed. Drop the video again to retry, or check AWS MediaConvert on the server.'}
            </Alert>
          </Col>
        )}

        <Col md={12}>
          <Row className="justify-content-center g-4 mb-1">
            <Col xs="auto">
              <MediaTile
                label="Video"
                iconClass="ri-smartphone-line"
                layout="portrait"
                hasContent={hasMediaFile}
                disabled={fileUploadDisabled}
                removeDisabled={isTargetBusy('file')}
                isRemoving={media.removingTarget === 'file'}
                isUploading={media.fileUploadStatus === 'uploading'}
                uploadProgress={media.fileUploadProgress ?? 0}
                uploadLabel="Uploading video…"
                emptyHint={hasTitle ? 'Drop vertical video' : 'Add a title first'}
                replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
                isDragActive={isFileDragActive}
                isDragAccept={isFileDragAccept}
                isDragReject={isFileDragReject}
                dragAcceptLabel="Drop video or audio"
                dragRejectLabel="Use video or audio only"
                getRootProps={getFileProps}
                getInputProps={getFileInputProps}
                onRemoveClick={hasMediaFile ? () => requestRemove('file') : undefined}
              >
                {media.file ? (
                  <PortraitMediaFrame poster={media.thumbnailPreview}>
                    <div className="text-center px-2">
                      <i className="ri-film-line fs-2 text-white d-block mb-2" />
                      <p className="mb-0 text-white small text-truncate">{media.file.name}</p>
                      {!contentId && (
                        <Badge color="warning" className="mt-2 fw-normal">
                          Pending save
                        </Badge>
                      )}
                    </div>
                  </PortraitMediaFrame>
                ) : media.existingFileUrl ? (
                  <>
                    {media.existingFileType === 'audio' && (
                      <div className="w-100 px-2">
                        <i className="ri-music-2-line fs-2 text-primary d-block text-center mb-2" />
                        <audio controls className="w-100" onClick={(e) => e.stopPropagation()}>
                          <source src={media.existingFileUrl} />
                        </audio>
                      </div>
                    )}
                    {media.existingFileType === 'video' && (
                      <PortraitMediaFrame poster={media.thumbnailPreview}>
                        <HlsVideoPlayer
                          src={media.existingFileUrl}
                          poster={media.thumbnailPreview}
                          controls
                          fit="contain"
                          className="w-100 h-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </PortraitMediaFrame>
                    )}
                    {(!media.existingFileType ||
                      (media.existingFileType !== 'audio' && media.existingFileType !== 'video')) && (
                      <a
                        href={media.existingFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Open file
                      </a>
                    )}
                  </>
                ) : null}
              </MediaTile>
            </Col>
            <Col xs="auto">
              <MediaTile
                label="Thumbnail"
                iconClass="ri-image-line"
                layout="thumbnail"
                hasContent={hasThumbnail}
                disabled={thumbnailUploadDisabled}
                removeDisabled={isTargetBusy('thumbnail')}
                isRemoving={media.removingTarget === 'thumbnail'}
                isUploading={media.thumbnailUploadStatus === 'uploading'}
                uploadProgress={media.thumbnailUploadProgress ?? 0}
                uploadLabel="Uploading thumbnail…"
                emptyHint={hasTitle ? 'Drop cover image (any orientation)' : 'Add a title first'}
                replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
                isDragActive={isThumbnailDragActive}
                isDragAccept={isThumbnailDragAccept}
                isDragReject={isThumbnailDragReject}
                dragAcceptLabel="Drop thumbnail"
                dragRejectLabel="Use an image file"
                getRootProps={getThumbnailProps}
                getInputProps={getThumbnailInputProps}
                onRemoveClick={hasThumbnail ? () => requestRemove('thumbnail') : undefined}
              >
                {media.thumbnailPreview ? (
                  <ThumbnailPreviewFrame src={media.thumbnailPreview} alt="Content thumbnail" />
                ) : media.thumbnail ? (
                  <ThumbnailPreviewFrame>
                    <div className="text-center px-2 text-muted">
                      <i className="ri-image-add-line fs-2 d-block mb-2" />
                      <p className="mb-0 small text-truncate">{media.thumbnail.name}</p>
                      {!contentId && (
                        <Badge color="warning" className="mt-2 fw-normal">
                          Pending save
                        </Badge>
                      )}
                    </div>
                  </ThumbnailPreviewFrame>
                ) : null}
              </MediaTile>
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal isOpen={confirmRemove !== null} toggle={() => setConfirmRemove(null)} centered>
        <ModalHeader toggle={() => setConfirmRemove(null)}>Remove media?</ModalHeader>
        <ModalBody>
          This will permanently remove the{' '}
          {confirmRemove === 'file' ? 'audio/video file' : 'thumbnail'} from the server.
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmRemoveMedia}>
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SingleContentMediaCard;
